import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * ADM -> Software queue si Software categories, calea noua. Perechile vechi,
 * din `application/controllers/Adm.php`:
 *
 *   softwarePending()           -> GET  /adm/software
 *   scanSoftware()              -> POST /adm/software/scan
 *   promoteSoftware()           -> POST /adm/software/promote
 *   ignoreSoftware()            -> POST /adm/software/ignore
 *   saveSoftwarePolicy()        -> POST /adm/software/policy
 *   softwareCategories()        -> GET  /adm/software/categories
 *   saveSoftwareCategory()      -> POST /adm/software/categories/save
 *   confirmSoftwareCategories() -> POST /adm/software/categories/confirm
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * Coada vine ODATA cu cadrul, ca in portal - acolo randurile erau randate pe
 * server. Sunt cel mult doua sute.
 */

export interface Option {
  value: string
  label: string
}

/** Un rand din coada de programe. */
export interface SoftwareRow {
  ID: number
  name: string
  version: string
  category: string
  level: number
  reasons: string
  state: number
  detected_at: string
  device_id: number
  device_name: string
  company_id: number
  company_name: string
}

/** Cat s-a departat catalogul furnizorului de cel din cod. */
export interface CatalogDrift {
  edited: string[]
  missing: string[]
  extra: string[]
}

export interface SoftwareShell {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  windows: number[]
  filters: { days: number; company_id: number; relay_device_id: number; run_type: number; state: number }
  states: Option[]
  state: number
  rows: SoftwareRow[]
  waiting: number
  drift: CatalogDrift
  /** Baga furnizorul asta programele in CMDB fara sa intrebe? */
  policy: boolean
  /**
   * Are furnizorul tipul de relatie `runs_on`?
   *
   * Fara el fiecare fisa de program intra atarnand de nimic, si analiza de
   * impact raspunde cu incredere si gresit. Ecranul o spune INAINTE.
   */
  canLink: boolean
  levels: Option[]
  canWrite: boolean
}

export interface Decision {
  status: string
  message: string
  ci_id?: number
}

/** Raspunsul unei treceri de scanare - cursorul vine inapoi de la server. */
export interface ScanStep extends Decision {
  scanned: number
  queued: number
  total: number
  next_offset: number
  promoted: number
  unlinked: number
  /** Fise de program vechi legate acum de masina lor (numai la primul pumn). */
  relinked?: number
}

export function softwareShellQuery(state: number) {
  return queryOptions({
    queryKey: ['adm', 'software', 'shell', state],
    queryFn: () => apiGet<SoftwareShell>(`/adm/software?sw_state=${state}`),
  })
}

export function promoteSoftware(id: number) {
  return apiPost<Decision>('/adm/software/promote', { id } satisfies Record<string, PostValue>)
}

export function ignoreSoftware(id: number) {
  return apiPost<Decision>('/adm/software/ignore', { id } satisfies Record<string, PostValue>)
}

export function saveSoftwarePolicy(on: boolean) {
  return apiPost<Decision>('/adm/software/policy', { auto_promote_software: on ? 1 : 0 })
}

/**
 * O trecere de scanare. Butonul o duce singur pana la capat, cu cursorul primit
 * inapoi: o cerere care cade lasa ecranul spunand cat s-a facut, nu o bara care
 * se invarte la nesfarsit.
 *
 * `company_id` se trimite, dar serverul il primeste numai daca e chiar firma din
 * jeton - o cerere facuta de mana nu poate scana flota altui client.
 */
export function scanSoftware(companyId: number, offset: number) {
  return apiPost<ScanStep>('/adm/software/scan', { company_id: companyId, offset })
}

/* ------------------------------------------------------------------ *
 * Podul de categorii
 * ------------------------------------------------------------------ */

/** O categorie a furnizorului, cu hotararea si sugestia langa ea. */
export interface CategoryRow {
  id: number
  name: string
  apps: number
  /** Cheia confirmata, sau null cand platforma hotaraste dupa nume. */
  category: string | null
  /** Ce s-ar potrivi - NU se aplica pana n-o confirma cineva. */
  suggested: string | null
}

/** Una din cele treisprezece categorii ale catalogului. */
export interface CatalogCategory {
  value: string
  label: string
  server: number
  client: number
  ceiling: number
  need: string
}

export interface CategoriesPage {
  pageTitle: string
  pageHeader: string
  breadcrumbs: string[]
  rows: CategoryRow[]
  suggested: number
  categories: CatalogCategory[]
  levels: Option[]
  canWrite: boolean
}

export function softwareCategoriesQuery() {
  return queryOptions({
    queryKey: ['adm', 'software', 'categories'],
    queryFn: () => apiGet<CategoriesPage>('/adm/software/categories'),
  })
}

/** Gol inseamna „platforma hotaraste dupa nume" - randul se sterge. */
export function saveSoftwareCategory(categoryId: number, category: string) {
  return apiPost<Decision>('/adm/software/categories/save', { category_id: categoryId, category })
}

export function confirmSoftwareCategories() {
  return apiPost<Decision & { done: number }>('/adm/software/categories/confirm', {})
}
